<template>
  <RevDrawer
    :back-button-label="i18n(modalTranslations.backButtonLabel)"
    :close-button-label="i18n(modalTranslations.close)"
    :has-back-button="!choiceStep"
    :name
    :title="modalTitle"
    @back="navigateToChoice"
    @close="handleClose"
  >
    <template #body>
      <div v-if="choiceStep">
        <RevList :has-external-borders="false">
          <RevListItemStatic class="cursor-pointer" @click="navigateToImport">
            <template #prefix> <IconUpload /> </template>
            <template #label>
              {{ i18n(translations.choiceStepImportTitle) }}
            </template>
            <template #description>
              {{ i18n(translations.choiceStepImportMessage) }}
            </template>
            <template #suffix>
              <IconChevronRight />
            </template>
          </RevListItemStatic>

          <RevListItemStatic class="cursor-pointer" @click="navigateToExport">
            <template #prefix> <IconDownload /> </template>
            <template #label>
              {{ i18n(translations.choiceStepExportTitle) }}
            </template>
            <template #description>
              {{ i18n(translations.choiceStepExportMessage) }}
            </template>
            <template #suffix>
              <IconChevronRight />
            </template>
          </RevListItemStatic>
        </RevList>
      </div>

      <div v-if="importStep" class="space-y-24">
        <form :id="formId" class="space-y-16" @submit.prevent="handleSubmit">
          <RevInputSelect
            id="encoding"
            v-model="importFormValues.encoding"
            :disabled="true"
            :label="i18n(translations.encodingLabel)"
            name="encoding"
            :options="encodingOptions"
          />
          <RevInputSelect
            id="delimiter"
            v-model="importFormValues.delimiter"
            :label="i18n(translations.delimiterLabel)"
            name="encoding"
            :options="delimiterOptions"
          />
          <div>
            <RevInputFileUpload
              id="csvFile"
              v-model="importFormValues.csvFile"
              :accept="CSV_FILE_EXTENSION"
              :label-empty="i18n(translations.csvFileLabel)"
              :max-files="1"
              @click="clearCsvFileErrors"
            >
            </RevInputFileUpload>
            <p
              v-if="csvFileErrors.length"
              class="text-static-danger-hi body-2 flex items-baseline pt-4 peer-disabled:text-static-danger-hi-disabled"
            >
              {{ formattedCsvFileError }}
            </p>
          </div>
        </form>
        <RevInfoBlock
          v-if="hasCsvTemplateInfos"
          :icon="IconLightbulb"
          :title="
            i18n(translations.importStepMessage, {
              count: metadata.csvColumns.length,
            })
          "
          variant="info"
        >
          <div v-if="metadata.csvColumns">
            <ol class="mb-4 mt-2 list-inside">
              <li v-for="field in metadata.csvColumns" :key="field">
                {{ field }}
              </li>
            </ol>
          </div>
          <p v-if="metadata.csvTemplateLink">
            <RevLink
              :to="metadata.csvTemplateLink"
              @click="handleTrackDownloadCsv"
            >
              {{ i18n(translations.importStepExample) }}
            </RevLink>
          </p>
          <p v-if="csvFaq" class="!mt-8">
            <RevLink target="_blank" :to="csvFaq" @click="handleTrackFaq">
              {{ i18n(translations.faqLink) }}
            </RevLink>
          </p>
        </RevInfoBlock>
      </div>

      <div v-if="exportStep">
        <form :id="formId" class="space-y-24" @submit.prevent="handleSubmit">
          <p>{{ i18n(translations.exportStepMessage) }}</p>
          <RevInputSelect
            id="exportEncoding"
            v-model="EXPORT_FORM_VALUE.encoding"
            :disabled="true"
            :label="i18n(translations.encodingLabel)"
            name="exportEncoding"
            :options="encodingOptions"
          />
        </form>
      </div>
    </template>

    <template #footer>
      <RevButton
        v-if="!choiceStep"
        :form="formId"
        full-width="always"
        :loading="isSaving"
        type="submit"
        variant="primary"
      >
        {{ buttonCta }}
      </RevButton>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'

import { type HttpApiError, buybackCatalogAPI } from '@backmarket/http-api'
import type { GetListingsImportMetadata } from '@backmarket/http-api/src/api-specs-catalog/buyback-catalog/buyback-catalog.types'
import { useBuybackConfig } from '@backmarket/nuxt-layer-buyback/composables/config/useBuybackConfig'
import modalTranslations from '@backmarket/nuxt-layer-buyback/utils/Modal.translations'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { type Option, RevInputSelect } from '@ds/components/InputSelect'
import { RevLink } from '@ds/components/Link'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconDownload } from '@ds/icons/IconDownload'
import { IconLightbulb } from '@ds/icons/IconLightbulb'
import { IconUpload } from '@ds/icons/IconUpload'

import {
  CSV_DELIMITERS,
  CSV_FILE_EXTENSION,
  ENCODING,
  EXPORT_FORM_VALUE,
  STEPS,
} from './CsvModal.constants'
import translations from './CsvModal.translations'
import type { CsvExportFormValues, CsvImportFormValues } from './CsvModal.types'

const props = withDefaults(
  defineProps<{
    name: string
    isExporting?: boolean
    isImporting?: boolean
  }>(),
  {
    isExporting: false,
    isImporting: false,
  },
)

const emit = defineEmits<{
  (e: 'submit-import', payload: CsvImportFormValues): void
  (e: 'submit-export', payload: CsvExportFormValues): void
}>()

const i18n = useI18n()
const logger = useLogger()
const tracking = useTracking()
const MARKETPLACE_CODE = useMarketplace().marketplace.marketplaceCode
const { csvFaq } = useBuybackConfig()

const currentStep = ref(STEPS.choice)
const importFormValues = reactive({
  encoding: ENCODING,
  csvFile: [],
  delimiter: '',
})
const csvFileErrors = ref<Array<string>>([])
const metadata = reactive<GetListingsImportMetadata>({
  csvColumns: [],
  csvTemplateLink: '',
})

const choiceStep = computed(() => currentStep.value === STEPS.choice)
const importStep = computed(() => currentStep.value === STEPS.import)
const exportStep = computed(() => currentStep.value === STEPS.export)
const modalTitle = computed(() => {
  const titles = {
    [STEPS.choice]: i18n(translations.title),
    [STEPS.import]: i18n(translations.importStepTitle),
    [STEPS.export]: i18n(translations.exportStepTitle),
  }

  return titles[currentStep.value]
})
const formId = computed(() => {
  const ids = {
    [STEPS.import]: 'importCsv',
    [STEPS.export]: 'exportCsv',
  }

  return ids[currentStep.value]
})
const requiredErrorMessage = computed(() => i18n(translations.required))
const formattedCsvFileError = computed(() => csvFileErrors.value[0])
const buttonCta = computed(() => {
  const ctas = {
    [STEPS.import]: i18n(translations.importStepCta),
    [STEPS.export]: i18n(translations.exportStepCta),
  }

  return ctas[currentStep.value]
})
const isSaving = computed(() => props.isExporting || props.isImporting)
const hasCsvTemplateInfos = computed(
  () => !isEmpty(metadata.csvColumns) || !isEmpty(metadata.csvTemplateLink),
)
const delimiterOptions = computed(
  () => CSV_DELIMITERS[MARKETPLACE_CODE] as Option[],
)
const encodingOptions = computed(() => [ENCODING])

async function fetchMetadata() {
  try {
    const data = await $httpFetch(buybackCatalogAPI.getListingsImportMetadata)
    const { csvColumns, csvTemplateLink } = data
    metadata.csvColumns = csvColumns
    metadata.csvTemplateLink = csvTemplateLink
  } catch (err) {
    // fails silently without showing info block

    logger.error('[BUYBACK][API][QUOTES] Error importing listings metadata', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-offer-front'],
    })
  }
}

function navigateToChoice() {
  currentStep.value = STEPS.choice
}

function navigateToImport() {
  fetchMetadata()
  currentStep.value = STEPS.import
}

function navigateToExport() {
  currentStep.value = STEPS.export
}

function handleSubmit() {
  if (currentStep.value === STEPS.import) {
    if (isEmpty(importFormValues.csvFile)) {
      csvFileErrors.value = [requiredErrorMessage.value]

      return
    }

    emit('submit-import', importFormValues)
  }

  if (currentStep.value === STEPS.export) {
    emit('submit-export', EXPORT_FORM_VALUE)
  }
}

function handleClose() {
  navigateToChoice()
  importFormValues.csvFile = []
}

function clearCsvFileErrors() {
  csvFileErrors.value = []
}

function handleTrackDownloadCsv() {
  tracking.trackClick({
    name: 'download_example_csv',
    zone: 'modal_refurbisher_wishlist',
  })
}

function handleTrackFaq() {
  tracking.trackClick({
    name: 'learn_more_csv',
    zone: 'modal_refurbisher_wishlist',
  })
}
</script>
